import { getCandidate } from '@/api/candidates';
import { getCertificatesList } from '@/api/certificates';
import { getEducationList } from '@/api/education';
import { getIndustries } from '@/api/industries';
import { getCityList, getRegions } from '@/api/regions';
import { getRolesList } from '@/api/roles';
import { getSeniorityList } from '@/api/seniority';
import { getSkillsList } from '@/api/skills';
import { getSpecializationList } from '@/api/specialization';

import type { CandidateInfo } from '@/types/candidate';
import type {
  Certificate,
  City,
  Education,
  Role,
  Seniority,
  Skill,
  Specialization,
} from '@/types/reference';

export type ReferenceBundle = {
  cities: City[];
  skills: Skill[];
  domains: Specialization[];
  seniority: Seniority[];
  certificates: Certificate[];
  education: Education[];
  roles?: Role[];
  candidate?: CandidateInfo | null;
};

export type ReferenceBundleOptions = {
  candidateId?: string;
  includeRoles?: boolean;
};

export type LocationReferenceBundle = {
  cities: City[];
  industries: { id: string; name: string }[];
  regions: { id: string; name: string }[];
};

export async function fetchReferenceBundle(
  options: ReferenceBundleOptions = {},
): Promise<ReferenceBundle> {
  const { candidateId, includeRoles = false } = options;

  const [
    candidate,
    roles,
    cities,
    skills,
    domains,
    seniority,
    certificates,
    education,
  ] = await Promise.all([
    candidateId
      ? getCandidate(candidateId)
      : Promise.resolve<CandidateInfo | null>(null),
    includeRoles ? getRolesList() : Promise.resolve(undefined),
    getCityList(),
    getSkillsList(),
    getSpecializationList(),
    getSeniorityList(),
    getCertificatesList(),
    getEducationList(),
  ]);

  return {
    cities,
    skills,
    domains,
    seniority,
    certificates,
    education,
    ...(includeRoles && roles ? { roles } : {}),
    ...(candidateId ? { candidate: candidate ?? null } : {}),
  };
}

export async function fetchLocationReferenceBundle(): Promise<LocationReferenceBundle> {
  const [cities, industries, regions] = await Promise.all([
    getCityList(),
    getIndustries(),
    getRegions(),
  ]);

  return { cities, industries, regions };
}
